import { useState } from "react";
import { Loader2, FileDown, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { authenticatedDownload } from "../lib/pdf";

/**
 * Year-end IRS Form 1099-NEC export dialog.
 *
 * Defaults to last calendar year (typical use: filing in Jan-Mar for prior
 * year), $600 IRS threshold. Both are user-overrideable. Clicking
 * "Download CSV" hits GET /api/vendors/1099-export?year=YYYY&threshold=NNN
 * and streams the file to disk. Vendors below the threshold are excluded
 * server-side; vendors with no tax_id are listed with a "MISSING — request
 * W-9" warning so the CPA / landlord can chase the W-9 before filing.
 */
export default function Export1099Dialog({ open, onClose }) {
  const now = new Date();
  // Default: previous calendar year (most common ask in Jan-Mar).
  const [year, setYear] = useState(now.getFullYear() - 1);
  const [threshold, setThreshold] = useState(600);
  const [downloading, setDownloading] = useState(false);

  // Reasonable year range: last 5 years + current year.
  const yearChoices = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) yearChoices.push(y);

  const download = async () => {
    setDownloading(true);
    try {
      await authenticatedDownload(
        "/api/vendors/1099-export",
        `1099-NEC-${year}.csv`,
        { year, threshold },
      );
      toast.success(`1099-NEC-${year}.csv downloaded`);
      onClose?.();
    } catch (e) {
      toast.error(`Download failed: ${e.message || e}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="export-1099-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <FileDown className="w-5 h-5 text-emerald-600" /> 1099-NEC export
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Generates a CPA-ready CSV of every vendor whose total compensation
            (work orders + direct expenses) crosses the IRS reporting threshold
            for the chosen year.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <label className="block">
            <div className="overline text-slate-600 mb-1">Tax year</div>
            <select
              className="input-clean w-full"
              data-testid="export-1099-year"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {yearChoices.map((y) => (
                <option key={y} value={y}>
                  {y}{y === now.getFullYear() - 1 ? " (most common)" : ""}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <div className="overline text-slate-600 mb-1">
              Reporting threshold ($)
            </div>
            <input
              type="number"
              min="0"
              step="50"
              className="input-clean w-full"
              data-testid="export-1099-threshold"
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
            />
            <div className="text-[11px] text-slate-400 mt-1">
              IRS minimum is $600. Set to 0 to include every vendor regardless
              of spend.
            </div>
          </label>

          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              Vendors with no Tax ID on file will appear in the CSV with a{" "}
              <span className="font-semibold">"MISSING — request W-9"</span>{" "}
              flag, so you (or your CPA) know exactly who to chase before
              filing.
            </div>
          </div>
        </div>

        <DialogFooter className="flex items-center gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="btn-outline-dark inline-flex items-center gap-1 px-3"
            data-testid="export-1099-cancel"
          >
            <X className="w-4 h-4" /> Cancel
          </button>
          <button
            type="button"
            onClick={download}
            disabled={downloading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-sm inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="export-1099-download"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4" />
            )}
            Download CSV
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
